import { useEffect } from 'react';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAsync } from '../hooks';

export const LAST_SITE_KEY = 'sb_last_site';
export const SITE_SECTIONS = [
  ['', 'Overview'],
  ['questions', 'Questions'],
  ['springboard', 'Springboard'],
  ['goals', 'Goals'],
  ['appearance', 'Appearance'],
  ['pages', 'Pages'],
  ['experiment', 'Experiment'],
] as const;

/** GTM-style site selector in the sidebar: switching keeps the current section where it exists. */
export function SiteSwitcher() {
  const navigate = useNavigate();
  const location = useLocation();
  const match = useMatch('/sites/:siteId/*');
  const siteId = match?.params.siteId && match.params.siteId !== 'new' ? match.params.siteId : '';
  const section = (match?.params['*'] ?? '').split('/')[0];
  // Re-read the list whenever the route changes so a newly added site appears without a reload.
  const { data } = useAsync(() => api.sites(), [location.pathname]);
  const sites = data?.sites ?? [];

  useEffect(() => {
    if (siteId) {
      try {
        localStorage.setItem(LAST_SITE_KEY, siteId);
      } catch {
        /* ignore */
      }
    }
  }, [siteId]);

  const onChange = (value: string) => {
    if (value === '__all') return navigate('/sites');
    if (value === '__new') return navigate('/sites/new');
    const keep = SITE_SECTIONS.some(([slug]) => slug && slug === section) ? `/${section}` : '';
    navigate(`/sites/${value}${keep}`);
  };

  return (
    <label className="site-switcher" title="Choose the site you are working on">
      <span className="navhead">Site</span>
      <select className="select" value={siteId || (location.pathname === '/sites/new' ? '__new' : '__all')} onChange={(e) => onChange(e.target.value)}>
        {sites.map((s) => (
          <option key={s.siteId} value={s.siteId}>
            {(s.brandName || s.domain).slice(0, 34)}
          </option>
        ))}
        {sites.length > 0 && <option disabled>──────────</option>}
        <option value="__all">All sites…</option>
        <option value="__new">Add a site…</option>
      </select>
    </label>
  );
}

/** Landing: the last site worked on, else the first site, else the add-site form. */
export function HomeRedirect() {
  const navigate = useNavigate();
  const { data, loading } = useAsync(() => api.sites(), []);
  useEffect(() => {
    if (loading || !data) return;
    let last: string | null = null;
    try {
      last = localStorage.getItem(LAST_SITE_KEY);
    } catch {
      /* ignore */
    }
    const target = data.sites.find((s) => s.siteId === last) ?? data.sites[0];
    navigate(target ? `/sites/${target.siteId}` : '/sites/new', { replace: true });
  }, [data, loading, navigate]);
  return <p className="muted">Loading…</p>;
}
